import { NetworkStatus } from "@apollo/client";
import { getUnixTime } from "date-fns/esm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteLoader from "react-window-infinite-loader";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useBulkActionsSelection from "components/BulkActions/useBulkActionsSelection";
import { useBulkActionsShiftSelection } from "components/BulkActions/useBulkActionsShiftSelection";
import NoAccessPage from "components/error/NoAccessPage";
import NotFoundPage from "components/error/NotFoundPage";
import {
  getFiltersPredicationFromURI,
  getSortOptionFromURI,
  makeConstraintByType,
} from "components/Filters/helpers";
import { SavedFilterView } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import ListEntitiesNew from "components/ListEntitiesNew";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { useBulkDismissNotifications } from "shared/Notification/useDismissNotification";
import {
  Notification,
  SearchNotificationsOutput,
  SearchQueryFieldConstraintTimeInLast,
  SearchSuggestionsFieldType,
} from "types/generated";
import { uniqByKey } from "utils/uniq";
import { AccountContext } from "views/AccountWrapper";

import NotificationsBulkActions from "./BulkActions";
import { INITIAL_PERIOD, initialSortDirection, initialSortOption, ITEMS_LIMIT } from "./constants";
import { NotificationsContext } from "./Context";
import NotificationsEmpty from "./Empty";
import FiltersLayout from "./FiltersLayout";
import { PERIODS } from "./FiltersLayout/constants";
import { SEARCH_NOTIFICATIONS } from "./gql";
import { getAllItemsForSelectAll } from "./helpers";
import NotificationVirtualizedListItem from "./ListItem/Virtualized";
import NotificationsPageLayout from "./PageLayout";

const Notifications = () => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const cachedNotificationEdges = useRef<Notification[]>([]);

  const {
    selectedSet,
    allSelected,
    onBulkSelectAll,
    onBulkResetAll,
    selectItem,
    unselectItem,
    syncAllSelectedItems,
    syncSelectedItemsWithVisibleOnes,
  } = useBulkActionsSelection();

  const isRefetching = useRef(false);
  const [currentDateRange, setCurrentDateRange] = useState<{
    start: Date;
    end: Date;
  }>(PERIODS[INITIAL_PERIOD].range);
  const [currentPeriod, setCurrentPeriod] = useState<
    SearchQueryFieldConstraintTimeInLast | undefined
  >(INITIAL_PERIOD);
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);
  const [dismissNotifications, { loading: dismissLoading }] = useBulkDismissNotifications({
    refetchQueries: ["NotificationsCount"],
  });

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, initialSortOption, initialSortDirection),
    [urlParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    if (currentDateRange) {
      predicatesMap?.set("lastOccurrenceAt", {
        field: "lastOccurrenceAt",
        exclude: null,
        constraint: makeConstraintByType(
          SearchSuggestionsFieldType.Time,
          typeof currentDateRange === "string"
            ? [currentDateRange]
            : [getUnixTime(currentDateRange.start), getUnixTime(currentDateRange.end)]
        ),
      });
    }

    return [...(predicatesMap?.values() || [])];
  }, [urlParams, currentDateRange]);

  const { viewer } = useTypedContext(AccountContext);
  const { onError } = useTypedContext(FlashContext);

  // Disable reloading when variables has changed to not remount entire sidebar
  const sortOptionFieldsRef = useRef(sortOptionFields);

  const {
    error,
    loading,
    data,
    fetchMore: fetchMoreNotifications,
    refetch,
    networkStatus,
  } = usePolledQuery<{
    searchNotifications: SearchNotificationsOutput;
    newNotificationsCount: number;
  }>(SEARCH_NOTIFICATIONS, {
    variables: {
      input: {
        first: ITEMS_LIMIT,
        after: null,
        ...(sortOptionFieldsRef.current && { orderBy: sortOptionFieldsRef.current }),
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
    // APOLLO CLIENT UPDATE
  });

  const [memoizedNotifications, memoizedNotificationsMap] = useMemo(() => {
    const sourceEdges = data?.searchNotifications?.edges.map((edge) => edge.node) || [];
    const edges = loading && !sourceEdges.length ? cachedNotificationEdges.current : sourceEdges;

    if (!loading) {
      cachedNotificationEdges.current = sourceEdges;
    }

    return [edges, new Map(edges.map((edge) => [edge.id, edge]))];
  }, [data?.searchNotifications?.edges, loading]);

  const notificationsQueryRefetch = async () => {
    try {
      isRefetching.current = true;
      await refetch({
        input: {
          first: ITEMS_LIMIT,
          after: null,
          fullTextSearch: searchInput,
          predicates,
          ...(sortOptionFields && { orderBy: sortOptionFields }),
        },
      });
    } catch (e) {
      onError(e);
    } finally {
      isRefetching.current = false;
    }
  };

  const loadMoreItems = async () => {
    try {
      if (
        data?.searchNotifications.pageInfo.endCursor &&
        data?.searchNotifications.pageInfo.hasNextPage
      ) {
        await fetchMoreNotifications({
          updateQuery: (prev, { fetchMoreResult }) => {
            if (fetchMoreResult && fetchMoreResult.searchNotifications.edges.length > 0) {
              return {
                ...prev,
                searchNotifications: {
                  ...fetchMoreResult.searchNotifications,
                  edges: uniqByKey(
                    [
                      ...(prev.searchNotifications.edges || []),
                      ...fetchMoreResult.searchNotifications.edges,
                    ],
                    "cursor"
                  ),
                },
              };
            }

            return prev;
          },
          variables: {
            input: {
              first: ITEMS_LIMIT,
              after: data.searchNotifications.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates,
              ...(sortOptionFields && { orderBy: sortOptionFields }),
            },
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  };

  const handleBulkActionsFinish = async () => {
    await notificationsQueryRefetch();
  };

  const handleBulkSelectAll = useCallback(() => {
    onBulkSelectAll(getAllItemsForSelectAll(memoizedNotifications));
  }, [memoizedNotifications, onBulkSelectAll]);

  const isItemLoaded = (value: number) => value < memoizedNotifications.length;

  const handleSetCurrentDateRange = (
    dateRange: { start: Date; end: Date },
    period?: SearchQueryFieldConstraintTimeInLast
  ) => {
    setCurrentDateRange(dateRange);
    setCurrentPeriod(period || undefined);
  };

  // filtering, refetch query with url params
  useEffect(() => {
    void notificationsQueryRefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, predicates, sortOptionFields]);

  // mark selected new loaded notifications if allSelected is true
  useEffect(() => {
    if (allSelected) {
      syncAllSelectedItems(getAllItemsForSelectAll(memoizedNotifications));
    }
  }, [allSelected, memoizedNotifications, syncAllSelectedItems]);

  // sync the selected items with the visible items on the list (filter out the ones that are not visible, for example in result of filters changing)
  useEffect(() => {
    if (selectedSet.size) {
      syncSelectedItemsWithVisibleOnes(memoizedNotificationsMap);
    }
  }, [memoizedNotificationsMap, selectedSet.size, syncSelectedItemsWithVisibleOnes]);

  const handleDismiss = (ids: Array<string>) =>
    dismissNotifications({ ids }).then(handleBulkActionsFinish);

  const onSelection = useBulkActionsShiftSelection({
    collection: memoizedNotifications,
    selectItem,
    unselectItem,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!viewer.admin) {
    return <NoAccessPage />;
  }

  if (
    !isRefetching.current &&
    networkStatus === NetworkStatus.loading &&
    loading &&
    !data?.searchNotifications
  ) {
    return (
      <NotificationsPageLayout>
        <PageLayoutSkeleton />
      </NotificationsPageLayout>
    );
  }

  if (
    !isRefetching.current &&
    networkStatus !== NetworkStatus.refetch &&
    !loading &&
    !data?.searchNotifications
  ) {
    return <NotFoundPage />;
  }

  const hasNoResults =
    searchInput.length > 0 || (predicates.length > 0 && currentPeriod !== INITIAL_PERIOD);

  return (
    <NotificationsContext.Provider
      value={{
        currentDateRange,
        setCurrentDateRange: handleSetCurrentDateRange,
      }}
    >
      <NotificationsPageLayout>
        <FiltersLayout
          predicates={predicates}
          allSelected={allSelected}
          onSelectAll={handleBulkSelectAll}
          onResetAll={onBulkResetAll}
          hasItems={memoizedNotifications.length > 0}
          currentSavedView={currentSavedView}
          setCurrentSavedView={setCurrentSavedView}
          newNotificationsCount={data?.newNotificationsCount}
        >
          {data && !memoizedNotifications.length && (
            <NotificationsEmpty hasNoResults={hasNoResults} />
          )}

          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={memoizedNotifications.length + ITEMS_LIMIT}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered }) => (
              <ListEntitiesNew
                itemCount={memoizedNotifications.length}
                itemProps={{
                  items: memoizedNotifications,
                  onCheckItem: onSelection,
                  selectedSet,
                }}
                virtualizedItem={NotificationVirtualizedListItem}
                itemKey={(index) => memoizedNotifications[index].id}
                onItemsRendered={onItemsRendered}
                outerContainerRef={virtualizedListContainerRef}
              />
            )}
          </InfiniteLoader>

          <NotificationsBulkActions
            virtualizedListContainerRef={virtualizedListContainerRef}
            selectedSet={selectedSet}
            notificationsMap={memoizedNotificationsMap}
            onBulkResetAll={onBulkResetAll}
            handleDismissNotifications={handleDismiss}
            onItemDismiss={unselectItem}
            dismissLoading={dismissLoading}
          />
        </FiltersLayout>
      </NotificationsPageLayout>
    </NotificationsContext.Provider>
  );
};

export default Notifications;
